import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Asterics,
  ButtonWrapper,
  DisClaimer,
  DisClaimerConfirmation,
  DisclaimerNote,
  DisClaimerRequired,
  DisclaimerText,
  DisclaimerWrapper,
  FooterWrapper,
  FormSection,
  HeaderBox,
  HeaderMessage,
  HeaderTitle,
  RowItemLabel,
  SettingsFooter
} from './BasicInformation';
import { Switch as ToggleSwitch, InformationTooltip, Checkbox, Button } from 'components';
import IcoInfo2 from 'icons/indexed-icons/IcoInfo2';
import theme from 'themes';
import Envelop from 'icons/indexed-icons/Mail';
import PhoneIcon from 'icons/indexed-icons/MobilePhone';
import MonitorScreenIcon from 'icons/indexed-icons/MonitorScreen';
import { useTranslation } from 'react-i18next';
import { UserNotifications } from 'types/user-management';
import {
  useGetUserBasicInformationQuery,
  useGetUserNotificationsQuery,
  useUpdateUserNotificationsMutation
} from 'api';
import { ToastMsg } from 'common/components/Toast';
import { UserObjectDeepEqual } from './UserSettingsUtility';
import CustomLoader from 'components/CustomLoader';
import { isValidPhoneNumber } from 'libphonenumber-js';

const Container = styled.div<{ isActive?: boolean }>`
  display: ${({ isActive }) => (isActive ? 'flex' : 'none')};
  flex-direction: column;
  height: 100%;
`;
const RowLabelWrapper = styled.div`
  display: flex;
`;
const NotificationContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 26rem);
  overflow: auto;
  margin-bottom: 1rem;
`;
const RowItemContainer = styled.div`
  display: flex;
  margin: 0 3.5rem;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  @media (max-width: 540px) {
    flex-direction: column;
    gap: 20px;
    align-items: flex-start;
    margin-top: 20px;
  }
`;
const RowDescription = styled.div`
  font-family: Roboto;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  margin-left: 52px;
`;
const SwitchBox = styled.div`
  width: auto;
  margin-right: 15px;
`;
const RowItem = styled.div<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: flex-end;
  gap: 8px;
  width: 192px;
  opacity: ${({ disabled }) => (disabled ? '.6' : 1)};
`;

const ColumnItem = styled.div`
  font-family: Roboto;
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
`;
const IconWrapper = styled.div`
  position: relative;
  top: 3px;
  width: 24px;
  height: 24px;
`;

type Props = {
  isActive?: boolean;
};
export const Notifications = ({ isActive }: Props): JSX.Element => {
  const { t } = useTranslation(['common']);
  const [notifications, setNotifications] = useState<UserNotifications[] | undefined>();
  const [notificationsTracked, setNotificationsTracked] = useState<
    UserNotifications[] | undefined
  >();
  const [isNotificationChanged, setIsNotificationChanged] = useState<boolean>(false);
  const [enableNotification, setEnableNotification] = useState<boolean>(false);
  const [confirmedDisclaimer, setConfirmedDisclaimer] = useState<boolean>(false);
  const {
    data: notify,
    isLoading,
    refetch: refetchUserNotification
  } = useGetUserNotificationsQuery();
  const { data: info } = useGetUserBasicInformationQuery();
  const isValidPhone = isValidPhoneNumber(info?.phone || '');

  const [updateNotifications] = useUpdateUserNotificationsMutation();

  useEffect(() => {
    if (notify) {
      setNotifications(notify.notifications);
      setNotificationsTracked(notify.notifications);
      setConfirmedDisclaimer(notify.isDisclaimerAccepted || false);
    }
  }, [notify, isActive]);
  useEffect(() => {
    // enable notification if any of the notification is selected
    const alert = notificationsTracked?.find(
      (notification) => notification.notificationArea === 'machine_alert'
    );
    setEnableNotification(
      alert?.emailEnabled || alert?.smsEnabled || alert?.pushAppEnabled ? true : false
    );
  }, [notificationsTracked]);

  useMemo(() => {
    const alert = notifications?.find(
      (notification) => notification.notificationArea === 'machine_alert'
    );
    const tracked = notificationsTracked?.find(
      (notification) => notification.notificationArea === 'machine_alert'
    );
    setIsNotificationChanged(
      UserObjectDeepEqual<UserNotifications>(
        alert as UserNotifications,
        tracked as UserNotifications
      )
    );
  }, [notifications, notificationsTracked]);
  const updateInfoOnFieldChange = (key: string, field: string, value: boolean) => {
    setNotifications(
      notifications?.map((notification) =>
        notification.notificationArea === key ? { ...notification, [field]: value } : notification
      )
    );
    setConfirmedDisclaimer(false);
  };
  const handleToggleSwitch = () => {
    if (enableNotification) {
      const updatedNotifications = notifications?.map((notification) =>
        notification.notificationArea === 'machine_alert'
          ? { ...notification, emailEnabled: false, smsEnabled: false, pushAppEnabled: false }
          : notification
      );
      setNotifications(updatedNotifications);
    }
    setEnableNotification(!enableNotification);
  };
  return (
    <>
      <Container isActive={isActive}>
        <HeaderBox>
          <HeaderTitle>{t('notifications')}</HeaderTitle>
          <HeaderMessage>{t('you_can_set_up_your_account_preferences')}</HeaderMessage>
        </HeaderBox>
        {isLoading ? (
          <CustomLoader size="3rem" />
        ) : (
          <>
            <NotificationContainer>
              <FormSection marginBottom="50">
                <RowLabelWrapper>
                  <SwitchBox>
                    <ToggleSwitch
                      checked={enableNotification}
                      onChange={() => {
                        handleToggleSwitch();
                      }}
                      height={16}
                      width={32}
                    />
                  </SwitchBox>
                  <RowItemLabel marginRight="7px">Alert</RowItemLabel>
                  <InformationTooltip
                    tooltipText={
                      'To get Alert notifications you’ll need to  provide Phone Number at the Basic Information section'
                    }
                    placement="top"
                    icon={<IcoInfo2 color={theme.colors.primaryBlue5} height={16} width={16} />}
                    width="20rem"
                    labelStyle={{
                      fontSize: '14px'
                    }}
                  />
                </RowLabelWrapper>
                <RowDescription>Receive notifications for machine Alerts</RowDescription>
                <RowItemContainer>
                  <RowItem>
                    <ColumnItem>
                      <Checkbox
                        key={'1'}
                        id={'chk1'}
                        width={18}
                        height={18}
                        margin="0"
                        disabled={!enableNotification}
                        checked={notifications?.some(
                          (notification) =>
                            notification.notificationArea === 'machine_alert' &&
                            notification.emailEnabled
                        )}
                        onChange={(evt) => {
                          updateInfoOnFieldChange(
                            'machine_alert',
                            'emailEnabled',
                            evt.target.checked
                          );
                          // updateInfoOnFieldChange('machine_alert', {
                          //   emailEnabled: evt.target.checked,
                          //   smsEnabled: notifications?.[0].smsEnabled,
                          //   pushAppEnabled: notifications?.[0].pushAppEnabled
                          // });
                        }}
                      />
                    </ColumnItem>
                    <ColumnItem>
                      <IconWrapper>
                        <Envelop width="20" height="20" top="-4" color="#000" />
                      </IconWrapper>
                    </ColumnItem>
                    <ColumnItem>Email</ColumnItem>
                  </RowItem>
                  <RowItem disabled={!enableNotification || !isValidPhone}>
                    <ColumnItem>
                      <Checkbox
                        key={'2'}
                        id={'chk2'}
                        width={18}
                        height={18}
                        margin="0"
                        disabled={!enableNotification || !isValidPhone}
                        checked={
                          isValidPhone &&
                          notifications?.some(
                            (notification) =>
                              notification.notificationArea === 'machine_alert' &&
                              notification.smsEnabled
                          )
                        }
                        onChange={
                          (evt) => {
                            updateInfoOnFieldChange(
                              'machine_alert',
                              'smsEnabled',
                              evt.target.checked
                            );
                          }
                          // updateInfoOnFieldChange('machine_alert', {
                          //   emailEnabled: notifications?.[0].emailEnabled,
                          //   smsEnabled: evt.target.checked,
                          //   pushAppEnabled: notifications?.[0].pushAppEnabled
                          // });
                        }
                      />
                    </ColumnItem>
                    <ColumnItem>
                      <IconWrapper>
                        <PhoneIcon />
                      </IconWrapper>
                    </ColumnItem>
                    <ColumnItem>SMS Text Notifications</ColumnItem>
                  </RowItem>
                  <RowItem>
                    <ColumnItem>
                      <Checkbox
                        key={'3'}
                        id={'chk3'}
                        width={18}
                        height={18}
                        margin="0"
                        disabled={!enableNotification}
                        checked={notifications?.some(
                          (notification) =>
                            notification.notificationArea === 'machine_alert' &&
                            notification.pushAppEnabled
                        )}
                        onChange={(evt) => {
                          updateInfoOnFieldChange(
                            'machine_alert',
                            'pushAppEnabled',
                            evt.target.checked
                          );
                          // updateInfoOnFieldChange('machine_alert', {
                          //   emailEnabled: notifications?.[0].emailEnabled,
                          //   smsEnabled: notifications?.[0].smsEnabled,
                          //   pushAppEnabled: evt.target.checked
                          // });
                        }}
                      />
                    </ColumnItem>
                    <ColumnItem>
                      <IconWrapper>
                        <MonitorScreenIcon />
                      </IconWrapper>
                    </ColumnItem>
                    <ColumnItem>Push App Notifications</ColumnItem>
                  </RowItem>
                </RowItemContainer>
              </FormSection>
              {/* <FormSection marginBottom="50">
              <RowLabelWrapper>
                <SwitchBox>
                  <ToggleSwitch
                    checked={true}
                    onChange={(checked) => checked}
                    height={16}
                    width={32}
                  />
                </SwitchBox>
                <RowItemLabel marginRight="7px">Parts Shop</RowItemLabel>
                <InformationTooltip
                  tooltipText={
                    'To get Alert notifications you’ll need to  provide Phone Number at the Basic Information section'
                  }
                  placement="top"
                  icon={<IcoInfo2 color={theme.colors.primaryBlue5} height={16} width={16} />}
                  width="20rem"
                  labelStyle={{
                    fontSize: '14px'
                  }}
                />
              </RowLabelWrapper>
              <RowDescription>Receive notifications for Orders</RowDescription>
              <RowItemContainer>
                <RowItem>
                  <ColumnItem>
                    <Checkbox
                      key={'1'}
                      width={18}
                      height={18}
                      margin="0"
                      checked={true}
                      onChange={() => {
                        console.log('checked');
                      }}
                    />
                  </ColumnItem>
                  <ColumnItem>
                    <Envelop width="20" height="20" />
                  </ColumnItem>
                  <ColumnItem>Email</ColumnItem>
                </RowItem>
                <RowItem>
                  <ColumnItem>
                    <Checkbox
                      key={'1'}
                      width={18}
                      height={18}
                      margin="0"
                      checked={true}
                      onChange={() => {
                        console.log('checked');
                      }}
                    />
                  </ColumnItem>
                  <ColumnItem>
                    <PhoneIcon />
                  </ColumnItem>
                  <ColumnItem>SMS Text Notifications</ColumnItem>
                </RowItem>
                <RowItem>
                  <ColumnItem>
                    <Checkbox
                      key={'1'}
                      width={18}
                      height={18}
                      margin="0"
                      checked={true}
                      onChange={() => {
                        console.log('checked');
                      }}
                    />
                  </ColumnItem>
                  <ColumnItem>
                    <MonitorScreenIcon />
                  </ColumnItem>
                  <ColumnItem>Push App Notifications</ColumnItem>
                </RowItem>
              </RowItemContainer>
            </FormSection>
            <FormSection marginBottom="50">
              <RowLabelWrapper>
                <SwitchBox>
                  <ToggleSwitch
                    checked={true}
                    onChange={(checked) => checked}
                    height={16}
                    width={32}
                  />
                </SwitchBox>
                <RowItemLabel marginRight="7px">Maintenance Manager</RowItemLabel>
                <InformationTooltip
                  tooltipText={
                    'To get Alert notifications you’ll need to  provide Phone Number at the Basic Information section'
                  }
                  placement="top"
                  icon={<IcoInfo2 color={theme.colors.primaryBlue5} height={16} width={16} />}
                  width="20rem"
                  labelStyle={{
                    fontSize: '14px'
                  }}
                />
              </RowLabelWrapper>
              <RowDescription>Receive notifications for machine inspections</RowDescription>
              <RowItemContainer>
                <RowItem>
                  <ColumnItem>
                    <Checkbox
                      key={'1'}
                      width={18}
                      height={18}
                      margin="0"
                      checked={true}
                      onChange={() => {
                        console.log('checked');
                      }}
                    />
                  </ColumnItem>
                  <ColumnItem>
                    <Envelop width="20" height="20" />
                  </ColumnItem>
                  <ColumnItem>Email</ColumnItem>
                </RowItem>
                <RowItem>
                  <ColumnItem>
                    <Checkbox
                      key={'1'}
                      width={18}
                      height={18}
                      margin="0"
                      checked={true}
                      onChange={() => {
                        console.log('checked');
                      }}
                    />
                  </ColumnItem>
                  <ColumnItem>
                    <PhoneIcon />
                  </ColumnItem>
                  <ColumnItem>SMS Text Notifications</ColumnItem>
                </RowItem>
                <RowItem>
                  <ColumnItem>
                    <Checkbox
                      key={'1'}
                      width={18}
                      height={18}
                      margin="0"
                      checked={true}
                      onChange={() => {
                        console.log('checked');
                      }}
                    />
                  </ColumnItem>
                  <ColumnItem>
                    <MonitorScreenIcon />
                  </ColumnItem>
                  <ColumnItem>Push App Notifications</ColumnItem>
                </RowItem>
              </RowItemContainer>
            </FormSection> */}
            </NotificationContainer>

            <SettingsFooter>
              <FooterWrapper>
                <ButtonWrapper>
                  <Button
                    variant="filled-primary"
                    size="small"
                    disabled={isNotificationChanged || !confirmedDisclaimer}
                    onClick={() => {
                      if (!notifications) return;
                      updateNotifications(notifications)
                        .unwrap()
                        .then(() => {
                          refetchUserNotification();
                          ToastMsg({
                            heading: 'Success',
                            type: 'success',
                            message: 'Notifications updated successfully'
                          });
                        })
                        .catch(() => {
                          ToastMsg({
                            heading: 'Error',
                            type: 'error',
                            message: 'Error updating Notifications'
                          });
                        });
                    }}
                  >
                    Save
                  </Button>
                </ButtonWrapper>

                <DisclaimerWrapper>
                  <DisClaimer>
                    <DisClaimerConfirmation>
                      <Checkbox
                        key={'1'}
                        id={'chk1'}
                        width={18}
                        height={18}
                        margin="0"
                        checked={confirmedDisclaimer}
                        onChange={() => {
                          setConfirmedDisclaimer(!confirmedDisclaimer);
                        }}
                      />
                    </DisClaimerConfirmation>
                    <DisclaimerText>
                      <DisclaimerNote>{t('notification_disclaimer_note')}</DisclaimerNote>
                      {t('notification_disclaimer')}
                    </DisclaimerText>
                  </DisClaimer>
                  <DisClaimerRequired>
                    <Asterics>*</Asterics>
                    {t('notification_required')}
                  </DisClaimerRequired>
                </DisclaimerWrapper>
              </FooterWrapper>
            </SettingsFooter>
          </>
        )}
        {/* <Spacer></Spacer> */}
      </Container>
    </>
  );
};
